// PostSuggestedListing.js

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  Alert,
  Platform,
  Image,
  FlatList,
  Dimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbayadmin.com/APIControllers';

const SCREEN_WIDTH = Dimensions.get('window').width;
const MAX_WIDTH = 720;
const IMAGE_BASE = 'https://collabbayadmin.com/listings/';
const FALLBACK_BANNER =
  'https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1';

const BANNER_WIDTH = Math.min(SCREEN_WIDTH - 32, MAX_WIDTH);
const BANNER_HEIGHT = SCREEN_WIDTH < 500 ? 190 : 250;

const parseBannerImages = (csv) => {
  if (!csv || !String(csv).trim()) return [FALLBACK_BANNER];

  const arr = String(csv)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((u) => (u.startsWith('http') ? u : IMAGE_BASE + u));

  return arr.length ? arr : [FALLBACK_BANNER];
};

export default function PostSuggestedListing({ navigation }) {
  const bannerListRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showLaunchOptions, setShowLaunchOptions] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [bannerImages, setBannerImages] = useState([FALLBACK_BANNER]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const [form, setForm] = useState({
    ListingID: '',
    BrandName: '',
    Location: '',
    City: '',
    Neighbourhood: '',
    ListingDescription: '',
    ContentBrief: '',
    NonCreditOffer: '',
    Credits: '',
    PreferredAvailability: '',
    OfferCategory: '',
    MinFollowersRequired: '',
    AdditionalBookingDetails: '',
    BrandURL: '',
    LogoImage: '',
    TurnAroundDays: '',
    PauseLiveToggle: '',
    isNudged: ''
  });

  useEffect(() => {
    fetchSuggestedListing();
  }, []);

  useEffect(() => {
    if (bannerImages.length <= 1) return;

    const interval = setInterval(() => {
      setBannerIndex((prev) => {
        const next = (prev + 1) % bannerImages.length;

        bannerListRef.current?.scrollToOffset({
          offset: next * BANNER_WIDTH,
          animated: true
        });

        return next;
      });
    }, 2600);

    return () => clearInterval(interval);
  }, [bannerImages.length]);

  const fetchSuggestedListing = async () => {
    try {
      setLoading(true);

      const endpoint = `${API_BASE}/GetBrandSuggestedListing.php?cacheBust=${Date.now()}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json'
        }
      });

      const rawText = await response.text();
      const data = JSON.parse(rawText);

      if (!data.success || !data.listing) {
        setOffer(null);
        setIsSubscribed(data?.isSubscribed === true);
        setLoading(false);
        return;
      }

      const listing = data.listing;

      setOffer(listing);
      setIsSubscribed(data.isSubscribed === true);

      const images = parseBannerImages(listing.LogoImage || '');
      setBannerImages(images);
      setBannerIndex(0);
      setLoadedImages({});

      setForm({
        ListingID: String(listing.ListingID || ''),
        BrandName: String(listing.BrandName || ''),
        Location: String(listing.Location || ''),
        City: String(listing.City || ''),
        Neighbourhood: String(listing.Neighbourhood || ''),
        ListingDescription: String(listing.ListingDescription || ''),
        ContentBrief: String(listing.ContentBrief || ''),
        NonCreditOffer: String(listing.NonCreditOffer || ''),
        Credits: String(listing.Credits || ''),
        PreferredAvailability: String(listing.PreferredAvailability || ''),
        OfferCategory: String(listing.OfferCategory || ''),
        MinFollowersRequired: String(listing.MinFollowersRequired || ''),
        AdditionalBookingDetails: String(listing.AdditionalBookingDetails || ''),
        BrandURL: String(listing.BrandURL || ''),
        LogoImage: String(listing.LogoImage || ''),
        TurnAroundDays: String(listing.TurnAroundDays || '4'),
        PauseLiveToggle: String(listing.PauseLiveToggle || ''),
        isNudged: String(listing.isNudged || '')
      });
    } catch (error) {
      console.log('Suggested listing fetch error:', error);
      setOffer(null);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const scrollToBanner = (index) => {
    if (!bannerListRef.current || bannerImages.length === 0) return;

    const next = ((index % bannerImages.length) + bannerImages.length) % bannerImages.length;

    bannerListRef.current.scrollToOffset({
      offset: next * BANNER_WIDTH,
      animated: true
    });

    setBannerIndex(next);
  };

  const onBannerScrollEnd = (event) => {
    const x = event?.nativeEvent?.contentOffset?.x || 0;
    const index = Math.round(x / BANNER_WIDTH);

    if (!Number.isNaN(index)) {
      setBannerIndex(index);
    }
  };

  const validateForm = () => {
    if (!form.NonCreditOffer.trim()) {
      Alert.alert('Missing offer', 'Please add what creators will receive.');
      return false;
    }

    if (!form.Credits.trim()) {
      Alert.alert('Missing value', 'Please add the estimated offer value.');
      return false;
    }

    if (!termsAccepted) {
      Alert.alert('Terms required', 'Please confirm you accept the collabbay™ terms before posting.');
      return false;
    }

    return true;
  };

  const postSuggestedListing = async () => {
    if (posting) return;
    if (!validateForm()) return;

    setPosting(true);

    setTimeout(() => {
      setPosting(false);

      if (isSubscribed) {
        Alert.alert('Offer ready', 'The post endpoint will be connected next.');
        return;
      }

      setShowLaunchOptions(true);
    }, 650);
  };

  const goToSubscribe = () => {
    setShowLaunchOptions(false);

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = 'https://collabbayadmin.com/subscribe';
      return;
    }

    navigation.navigate('PaymentPage');
  };

  const goToMyOffers = () => {
    setShowLaunchOptions(false);
    navigation.navigate('MyOffers', { forceRefresh: true });
  };

  if (loading) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingTitle}>Loading your suggested offer</Text>
        <Text style={styles.loadingSub}>Getting your prepared listing ready...</Text>
      </View>
    );
  }

  if (!offer) {
    return (
      <View style={styles.emptyWrapper}>
        <View style={styles.emptyCard}>
          <View style={styles.emptyIcon}>
            <Ionicons name="document-text-outline" size={28} color="#111" />
          </View>

          <Text style={styles.emptyTitle}>No suggested offer found</Text>

          <Text style={styles.emptyText}>
            If collabbay™ prepares a suggested listing for your account, it will appear here.
          </Text>

          <TouchableOpacity style={styles.primaryButton} onPress={fetchSuggestedListing}>
            <Text style={styles.primaryButtonText}>Refresh</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('BusinessHomepage')}
          >
            <Text style={styles.secondaryButtonText}>Back to dashboard</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.page} showsVerticalScrollIndicator={false}>
        <View style={styles.topRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="chevron-back" size={20} color="#111" />
          </TouchableOpacity>

          <View style={styles.topPill}>
            <Ionicons name="sparkles-outline" size={14} color="#111" />
            <Text style={styles.topPillText}>Suggested for you</Text>
          </View>
        </View>

        <View style={styles.bannerCard}>
          <FlatList
            ref={bannerListRef}
            data={bannerImages}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item, index) => item + index}
            getItemLayout={(_, index) => ({
              length: BANNER_WIDTH,
              offset: BANNER_WIDTH * index,
              index
            })}
            onMomentumScrollEnd={onBannerScrollEnd}
            style={styles.bannerList}
            renderItem={({ item, index }) => (
              <View style={styles.bannerImageWrap}>
                {!loadedImages[index] && <View style={styles.imageSkeleton} />}

                <Image
                  source={{ uri: item }}
                  style={[
                    styles.bannerImage,
                    { display: loadedImages[index] ? 'flex' : 'none' }
                  ]}
                  resizeMode="cover"
                  onLoad={() =>
                    setLoadedImages((prev) => ({
                      ...prev,
                      [index]: true
                    }))
                  }
                  onError={() =>
                    setLoadedImages((prev) => ({
                      ...prev,
                      [index]: true
                    }))
                  }
                />
              </View>
            )}
          />

          {bannerImages.length > 1 && (
            <>
              <TouchableOpacity
                style={styles.bannerArrow}
                onPress={() => scrollToBanner(bannerIndex + 1)}
              >
                <Ionicons name="chevron-forward" size={19} color="#111" />
              </TouchableOpacity>

              <View style={styles.dotsWrap}>
                {bannerImages.map((_, index) => (
                  <TouchableOpacity key={index} onPress={() => scrollToBanner(index)}>
                    <View style={[styles.dot, index === bannerIndex && styles.dotActive]} />
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
        </View>

        <View style={styles.infoCard}>
          <View style={styles.infoIconCircle}>
            <Ionicons name="information-circle-outline" size={22} color="#1f5d8f" />
          </View>

          <View style={{ flex: 1 }}>
            <Text style={styles.infoTitle}>This offer was prepared using our data.</Text>
            <Text style={styles.infoText}>
              Based on your venue details and the types of creator offers that tend to perform best, our agents have prepared a suggested listing for you.
            </Text>
            <Text style={styles.infoText}>
              Once you post, just sit back, wait for requests to come in, and you’re good to go.
            </Text>
          </View>
        </View>

        <View style={styles.introCard}>
          <Text style={styles.kicker}>Prepared by collabbay™</Text>
          <Text style={styles.title}>Your suggested offer is ready.</Text>

          <Text style={styles.bodyText}>
            Review the offer below. You can keep our suggestion as it is, or make quick edits before posting.
          </Text>
        </View>

        <View style={styles.summaryCard}>
          <View style={styles.cardHeaderRow}>
            <Text style={styles.cardTitle}>Offer overview</Text>
            <View style={styles.softBadge}>
              <Text style={styles.softBadgeText}>Suggested</Text>
            </View>
          </View>

          <View style={styles.offerBox}>
            <View style={styles.offerIcon}>
              <Ionicons name="gift-outline" size={20} color="#111" />
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.offerLabel}>Suggested creator gift</Text>
              <Text style={styles.offerValue}>{form.NonCreditOffer || 'Not set'}</Text>
            </View>
          </View>

          <View style={styles.valueRow}>
            <View style={styles.valueTile}>
              <Ionicons name="wallet-outline" size={19} color="#111" />
              <Text style={styles.valueLabel}>Offer value</Text>
              <Text style={styles.valueNumber}>£{form.Credits || '0'}</Text>
            </View>

            <View style={styles.valueTile}>
              <Ionicons name="camera-outline" size={19} color="#111" />
              <Text style={styles.valueLabel}>Creator deliverable</Text>
              <Text style={styles.valueNumberSmall}>1 reel/post</Text>
            </View>
          </View>
        </View>

        <View style={styles.formCard}>
          <View style={styles.sectionHeaderBlock}>
            <Text style={styles.cardTitle}>Review and edit</Text>
            <Text style={styles.helperText}>
              These are the main details creators will see. We’ve suggested what we think is best, but you can amend anything before posting.
            </Text>
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>What creators receive</Text>
            <TextInput
              value={form.NonCreditOffer}
              onChangeText={(value) => updateField('NonCreditOffer', value)}
              style={styles.input}
              placeholder="E.g. 2 mains, 2 drinks"
              placeholderTextColor="#888"
              multiline
            />
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Offer value (£)</Text>
            <TextInput
              value={form.Credits}
              onChangeText={(value) => updateField('Credits', value.replace(/[^0-9]/g, ''))}
              style={styles.input}
              placeholder="E.g. 70"
              placeholderTextColor="#888"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Content brief</Text>
            <TextInput
              value={form.ContentBrief}
              onChangeText={(value) => updateField('ContentBrief', value)}
              style={[styles.input, styles.textArea]}
              placeholder="What should creators include?"
              placeholderTextColor="#888"
              multiline
            />
          </View>

          <View style={styles.divider} />

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Location</Text>
            <TextInput
              value={form.Location}
              onChangeText={(value) => updateField('Location', value)}
              style={styles.input}
              placeholder="Venue address"
              placeholderTextColor="#888"
            />
          </View>

          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Short description</Text>
            <TextInput
              value={form.ListingDescription}
              onChangeText={(value) => updateField('ListingDescription', value)}
              style={[styles.input, styles.largeTextArea]}
              placeholder="Describe your venue"
              placeholderTextColor="#888"
              multiline
            />
          </View>

          <View style={styles.recapCard}>
            <View style={styles.recapTopRow}>
              <View style={styles.recapIcon}>
                <Ionicons name="checkmark-circle-outline" size={21} color="#111" />
              </View>
              <Text style={styles.recapTitle}>What happens after posting?</Text>
            </View>

            <Text style={styles.recapText}>
              Post this offer, accept creators you like, and only when you accept a creator will you provide:
            </Text>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>{form.NonCreditOffer || 'your listed offer'} in exchange for 1 reel/post.</Text>
            </View>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>Content is due in {form.TurnAroundDays || '4'} days.</Text>
            </View>

            <View style={styles.recapBullet}>
              <Text style={styles.recapBulletDot}>•</Text>
              <Text style={styles.recapBulletText}>You stay in control — creator requests still need your approval.</Text>
            </View>
          </View>

          <TouchableOpacity
            activeOpacity={0.86}
            style={styles.checkboxRow}
            onPress={() => setTermsAccepted(!termsAccepted)}
          >
            <View style={[styles.checkbox, termsAccepted && styles.checkboxActive]}>
              {termsAccepted && <Ionicons name="checkmark" size={15} color="#111" />}
            </View>

            <Text style={styles.checkboxText}>
              I accept the collabbay™ terms of use and consent to this offer being shown publicly in the marketplace.
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            activeOpacity={0.9}
            style={[styles.postButton, posting && { opacity: 0.65 }]}
            onPress={postSuggestedListing}
            disabled={posting}
          >
            {posting ? (
              <ActivityIndicator color="#111" />
            ) : (
              <>
                <Text style={styles.postButtonText}>Looks good, post my offer</Text>
                <Ionicons name="arrow-forward" size={18} color="#111" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Modal visible={showLaunchOptions} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.launchModal}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.modalIcon}>
                <Ionicons name="checkmark-circle-outline" size={54} color="#1f8f3a" />
              </View>

              <Text style={styles.modalTitle}>Your offer is ready to launch</Text>
              <Text style={styles.modalSubText}>
                Choose how you want your listing to sit in the marketplace.
              </Text>

              <TouchableOpacity activeOpacity={0.9} style={styles.standardCard} onPress={goToSubscribe}>
                <View style={styles.optionHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.optionTitle}>Standard Listing</Text>
                    <Text style={styles.optionTag}>Recommended</Text>
                  </View>

                  <View style={styles.tryBadge}>
                    <Text style={styles.tryBadgeText}>TRY</Text>
                  </View>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="star-outline" size={18} color="#111" />
                  <Text style={styles.featureText}>My offer gets premium creator spaces</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="trending-up-outline" size={18} color="#111" />
                  <Text style={styles.featureText}>My offer gets priority ranking among 200+ offers</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="people-outline" size={18} color="#111" />
                  <Text style={styles.featureText}>I can handpick and invite creators to my offer</Text>
                </View>

                <View style={styles.standardButton}>
                  <Text style={styles.standardButtonText}>Post as Standard</Text>
                </View>
              </TouchableOpacity>

              <TouchableOpacity activeOpacity={0.9} style={styles.restrictedCard} onPress={goToMyOffers}>
                <View style={styles.optionHeader}>
                  <View>
                    <Text style={styles.optionTitle}>Restricted Listing</Text>
                    <Text style={styles.optionTagMuted}>Basic access</Text>
                  </View>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="checkmark-circle-outline" size={18} color="#333" />
                  <Text style={styles.restrictedText}>Creators can view and book</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="remove-circle-outline" size={18} color="#777" />
                  <Text style={styles.mutedFeatureText}>Restricted visibility</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="remove-circle-outline" size={18} color="#777" />
                  <Text style={styles.mutedFeatureText}>Capped booking volume</Text>
                </View>

                <View style={styles.restrictedButton}>
                  <Text style={styles.restrictedButtonText}>Continue as Restricted</Text>
                </View>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f3f3f3'
  },

  page: {
    width: '100%',
    maxWidth: MAX_WIDTH,
    alignSelf: 'center',
    padding: 16,
    paddingBottom: 56
  },

  loadingWrapper: {
    flex: 1,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24
  },

  loadingTitle: {
    marginTop: 14,
    fontSize: 16,
    fontWeight: '800',
    color: '#111'
  },

  loadingSub: {
    marginTop: 4,
    fontSize: 13,
    color: '#666',
    fontWeight: '500'
  },

  emptyWrapper: {
    flex: 1,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18
  },

  emptyCard: {
    backgroundColor: '#fff',
    borderRadius: 28,
    padding: 24,
    alignItems: 'center',
    width: '100%',
    maxWidth: 440,
    borderWidth: 1,
    borderColor: '#e9e9e9'
  },

  emptyIcon: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12
  },

  emptyTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#111',
    textAlign: 'center'
  },

  emptyText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    lineHeight: 19,
    marginTop: 8
  },

  primaryButton: {
    marginTop: 18,
    backgroundColor: '#fdf05d',
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 22,
    borderWidth: 1,
    borderColor: '#e8dd00'
  },

  primaryButtonText: {
    fontWeight: '900',
    color: '#111'
  },

  secondaryButton: {
    marginTop: 10,
    backgroundColor: '#f3f3f3',
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 22
  },

  secondaryButtonText: {
    fontWeight: '800',
    color: '#111'
  },

  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12
  },

  backButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  topPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 13,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  topPillText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '900',
    color: '#111'
  },

  bannerCard: {
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT,
    alignSelf: 'center',
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: '#e9e9e9',
    marginBottom: 14
  },

  bannerList: {
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT
  },

  bannerImageWrap: {
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT,
    backgroundColor: '#e9e9e9'
  },

  bannerImage: {
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT
  },

  imageSkeleton: {
    position: 'absolute',
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT,
    backgroundColor: '#e1e1e1'
  },

  bannerArrow: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -18 }],
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)',
    alignItems: 'center',
    justifyContent: 'center'
  },

  dotsWrap: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center'
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.7)',
    marginHorizontal: 3
  },

  dotActive: {
    width: 16,
    backgroundColor: '#fdf05d'
  },

  infoCard: {
    backgroundColor: '#eef7ff',
    borderColor: '#cfe7ff',
    borderWidth: 1,
    borderRadius: 22,
    padding: 15,
    marginBottom: 14,
    flexDirection: 'row',
    alignItems: 'flex-start'
  },

  infoIconCircle: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#dff0ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 11
  },

  infoTitle: {
    fontSize: 14,
    fontWeight: '900',
    color: '#12334f',
    marginBottom: 5
  },

  infoText: {
    fontSize: 13,
    color: '#29445c',
    lineHeight: 19,
    fontWeight: '600',
    marginBottom: 4
  },

  introCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 20,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#e9e9e9'
  },

  kicker: {
    fontSize: 12,
    fontWeight: '900',
    color: '#777',
    marginBottom: 6
  },

  title: {
    fontSize: 27,
    fontWeight: '900',
    color: '#111',
    lineHeight: 32,
    marginBottom: 10
  },

  bodyText: {
    fontSize: 14,
    color: '#555',
    lineHeight: 21,
    fontWeight: '500',
    marginBottom: 8
  },

  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#e9e9e9'
  },

  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12
  },

  cardTitle: {
    fontSize: 19,
    fontWeight: '900',
    color: '#111'
  },

  softBadge: {
    backgroundColor: '#f3f3f3',
    paddingVertical: 7,
    paddingHorizontal: 11,
    borderRadius: 999
  },

  softBadgeText: {
    fontSize: 11,
    fontWeight: '900',
    color: '#555'
  },

  offerBox: {
    backgroundColor: '#fdf05d',
    borderRadius: 18,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12
  },

  offerIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.75)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 11
  },

  offerLabel: {
    fontSize: 12,
    color: '#333',
    fontWeight: '900',
    marginBottom: 4
  },

  offerValue: {
    fontSize: 16,
    color: '#111',
    fontWeight: '800',
    lineHeight: 22
  },

  valueRow: {
    flexDirection: SCREEN_WIDTH < 520 ? 'column' : 'row',
    gap: 10
  },

  valueTile: {
    flex: 1,
    backgroundColor: '#f3f3f3',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#ececec'
  },

  valueLabel: {
    marginTop: 8,
    fontSize: 12,
    color: '#666',
    fontWeight: '800'
  },

  valueNumber: {
    marginTop: 3,
    fontSize: 22,
    fontWeight: '900',
    color: '#111'
  },

  valueNumberSmall: {
    marginTop: 5,
    fontSize: 15,
    fontWeight: '900',
    color: '#111'
  },

  formCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    borderWidth: 1,
    borderColor: '#e9e9e9'
  },

  sectionHeaderBlock: {
    backgroundColor: '#fafafa',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eeeeee',
    marginBottom: 14
  },

  helperText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
    marginTop: 4
  },

  fieldGroup: {
    marginBottom: 4
  },

  label: {
    fontSize: 13,
    fontWeight: '900',
    color: '#222',
    marginBottom: 7
  },

  input: {
    backgroundColor: '#f3f3f3',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#ececec',
    padding: 14,
    fontSize: 14,
    color: '#111',
    marginBottom: 14,
    minHeight: 48
  },

  textArea: {
    minHeight: 92,
    textAlignVertical: 'top'
  },

  largeTextArea: {
    minHeight: 122,
    textAlignVertical: 'top'
  },

  divider: {
    height: 1,
    backgroundColor: '#eeeeee',
    marginVertical: 8
  },

  recapCard: {
    backgroundColor: '#fafafa',
    borderRadius: 20,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eeeeee',
    marginTop: 2,
    marginBottom: 14
  },

  recapTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },

  recapIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 9
  },

  recapTitle: {
    fontSize: 15,
    color: '#111',
    fontWeight: '900',
    flex: 1
  },

  recapText: {
    fontSize: 13,
    color: '#555',
    lineHeight: 19,
    fontWeight: '600',
    marginBottom: 8
  },

  recapBullet: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 5
  },

  recapBulletDot: {
    fontSize: 15,
    fontWeight: '900',
    color: '#111',
    marginRight: 7,
    lineHeight: 19
  },

  recapBulletText: {
    flex: 1,
    fontSize: 13,
    color: '#333',
    lineHeight: 19,
    fontWeight: '700'
  },

  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f8f8f8',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: '#ececec',
    marginTop: 2,
    marginBottom: 15
  },

  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#111',
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff'
  },

  checkboxActive: {
    backgroundColor: '#fdf05d'
  },

  checkboxText: {
    flex: 1,
    fontSize: 12,
    color: '#444',
    lineHeight: 18,
    fontWeight: '600'
  },

  postButton: {
    backgroundColor: '#fdf05d',
    borderRadius: 18,
    paddingVertical: 16,
    paddingHorizontal: 18,
    borderWidth: 1,
    borderColor: '#e8dd00',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },

  postButtonText: {
    fontSize: 15,
    fontWeight: '900',
    color: '#111',
    marginRight: 7
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 14
  },

  launchModal: {
    backgroundColor: '#fff',
    borderRadius: 28,
    width: '100%',
    maxWidth: 620,
    maxHeight: '92%',
    padding: 20,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  modalIcon: {
    alignItems: 'center',
    marginBottom: 4
  },

  modalTitle: {
    fontSize: 23,
    fontWeight: '900',
    textAlign: 'center',
    color: '#111',
    marginBottom: 6
  },

  modalSubText: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 16
  },

  standardCard: {
    backgroundColor: '#effff3',
    borderColor: '#cdebd3',
    borderWidth: 1,
    borderRadius: 22,
    padding: 16,
    marginBottom: 12
  },

  restrictedCard: {
    backgroundColor: '#f8f8f8',
    borderColor: '#e8e8e8',
    borderWidth: 1,
    borderRadius: 22,
    padding: 16
  },

  optionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 13
  },

  optionTitle: {
    fontSize: 19,
    fontWeight: '900',
    color: '#111'
  },

  optionTag: {
    fontSize: 12,
    color: '#2f6f3e',
    fontWeight: '900',
    marginTop: 2
  },

  optionTagMuted: {
    fontSize: 12,
    color: '#666',
    fontWeight: '800',
    marginTop: 2
  },

  tryBadge: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingHorizontal: 13,
    paddingVertical: 8
  },

  tryBadgeText: {
    color: '#fdf05d',
    fontWeight: '900',
    fontSize: 12
  },

  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 9
  },

  featureText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#111',
    lineHeight: 18,
    fontWeight: '800'
  },

  restrictedText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
    fontWeight: '700'
  },

  mutedFeatureText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#777',
    lineHeight: 18,
    fontWeight: '600'
  },

  standardButton: {
    backgroundColor: '#fdf05d',
    borderRadius: 15,
    paddingVertical: 14,
    marginTop: 7,
    borderWidth: 1,
    borderColor: '#e8dd00'
  },

  standardButtonText: {
    textAlign: 'center',
    fontWeight: '900',
    color: '#111'
  },

  restrictedButton: {
    backgroundColor: '#fff',
    borderRadius: 15,
    paddingVertical: 14,
    marginTop: 7,
    borderWidth: 1,
    borderColor: '#e2e2e2'
  },

  restrictedButtonText: {
    textAlign: 'center',
    fontWeight: '900',
    color: '#111'
  }
});